import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FieldType, TranslationKey } from "../types/modulesInterfaces";
import i18n from "../i18n";

export const capitalize = (str: string | null) => {
	return str ? str.charAt(0).toUpperCase() + str.slice(1) : undefined;
};

export const capitalizeEach = (str: string) =>
	str ? str
		?.split(" ")
		.map((word) => capitalize(word))
		.join(" ") : undefined;

export function isFetchBaseQueryError(
	error: unknown,
): error is FetchBaseQueryError {
	return (
		typeof error === "object" &&
		error != null &&
		"status" in error &&
		"data" in error
	);
}

export const isSWW = (inputType: FieldType) =>
	[FieldType.SWW, FieldType.SWW_BOOL, FieldType.SWW_SELECT, FieldType.SWW_T2].includes(inputType);


export const translate = (key: TranslationKey) => {
	return i18n.t(key);
}

export const formatNumber = (num: number | string, decimalPlaces = 2): string => {
	let fixed
	if (typeof num === "string") {
		fixed = parseInt(parseInt(num).toFixed(decimalPlaces))
	} else {
		fixed = parseInt(num.toFixed(decimalPlaces));
	}
	const options = { maximumFractionDigits: 2 }
	const formattedNumber = Intl.NumberFormat("en-US", options).format(fixed);
	return formattedNumber
}

export const readableFileSize = (attachmentSize: number) => {
	const DEFAULT_SIZE = 0;
	const fileSize = attachmentSize ?? DEFAULT_SIZE;

	if (!fileSize) {
		return `${DEFAULT_SIZE} kb`;
	}

	const sizeInKb = fileSize / 1024;

	if (sizeInKb > 1024) {
		return `${(sizeInKb / 1024).toFixed(2)} mb`;
	} else if (sizeInKb > 1) {
		return `${sizeInKb.toFixed(2)} kb`;
	} else {
		return `${fileSize} bytes`;
	}
}

export function getCapitalizationYears(startYear: number | null | undefined, implementationPhase: number | null | undefined, lastYearOfAccounting: number | null | undefined) {
    if (lastYearOfAccounting === null || lastYearOfAccounting === undefined || startYear === null || startYear === undefined || implementationPhase === null || implementationPhase === undefined) return null
    return lastYearOfAccounting - (startYear + implementationPhase)
}

export function formatDefaultNumbers(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  
  // Check if it's a non-zero value close to zero
  if (value !== 0 && Math.abs(value) < 0.01) {
    // For very small numbers, use scientific notation or find first significant digits
    const valueStr = value.toString();
    const match = valueStr.match(/0\.0*[1-9][0-9]{0,2}/);
    if (match) {
      // Return the number with the first 3 significant digits
      return match[0];
    }
    
    // Alternative: use scientific notation with 3 significant digits
    return value.toExponential(2);
  }
  
  // For normal numbers, format with 2 decimal places
  return value.toFixed(2);
}

// Reads the file name from a Content-Disposition header, preferring the RFC 5987
// `filename*=UTF-8''…` form (used for non-ASCII names) over `filename="…"`.
export function fileNameFromContentDisposition(header: string | null | undefined): string | null {
	if (!header) return null;
	const encoded = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1];
	if (encoded) {
		try {
			return decodeURIComponent(encoded.trim().replace(/^"|"$/g, ""));
		} catch {
			// Malformed encoding: fall back to the plain filename below.
		}
	}
	return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}
